'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Alert, Box, Grid, type SelectChangeEvent } from '@mui/material';
import VtkViewer, { type VtkViewerHandle } from '../components/VtkViewer';
import ControlPanel, { type ViewFieldKey } from '../components/controlPanel';
import SectionViewer from '../components/sectionViewer';
import type { VtkDatasetInfo, VtkPolylineSet } from '../types/vtk';
import { computeScalarRange, type ScalarRange } from '../lib/vtkScalarStyling';

interface BackendPolyDataResponse {
  message: string;
  mesh: VtkDatasetInfo;
  activeScalar?: string;
}

interface BackendStreamlineResponse {
  message: string;
  streamlines: VtkPolylineSet | null;
  colorScalar?: string | null;
}

const FIELD_CONFIG: Record<
  ViewFieldKey,
  {
    label: string;
    mode: 'streamlines' | 'scalar';
    scalarField: string;
    vectorField?: string;
    activeScalarLabel: string;
  }
> = {
  control_velocity_magnitude: {
    label: 'Controlled Velocity Magnitude',
    mode: 'scalar',
    scalarField: 'mag_cont_vel',
    activeScalarLabel: 'Controlled Velocity Magnitude',
  },
  adjoint_velocity_magnitude: {
    label: 'Adjoint Velocity Magnitude',
    mode: 'scalar',
    scalarField: 'mag_adj_vel',
    activeScalarLabel: 'Adjoint Velocity Magnitude',
  },
  control_velocity: {
    label: 'Controlled Velocity',
    mode: 'streamlines',
    scalarField: 'mag_cont_vel',
    vectorField: 'cont_vel',
    activeScalarLabel: 'Controlled Velocity Magnitude',
  },
  adjoint_velocity: {
    label: 'Adjoint Velocity',
    mode: 'streamlines',
    scalarField: 'mag_adj_vel',
    vectorField: 'adj_vel',
    activeScalarLabel: 'Adjoint Velocity Magnitude',
  },
  control_pressure: {
    label: 'Controlled Pressure',
    mode: 'scalar',
    scalarField: 'cont_p',
    activeScalarLabel: 'Controlled Pressure',
  },
  adjoint_pressure: {
    label: 'Adjoint Pressure',
    mode: 'scalar',
    scalarField: 'adj_p',
    activeScalarLabel: 'Adjoint Pressure',
  },
};

function extractScalarRange(
  dataset: VtkDatasetInfo | null,
  scalarName: string,
): ScalarRange | null {
  if (!dataset) return null;

  const scalarEntry = dataset.pointData?.[scalarName];
  if (!scalarEntry) return null;
  if (scalarEntry.numberOfComponents !== 1) return null;
  if (!Array.isArray(scalarEntry.values) || scalarEntry.values.length === 0) {
    return null;
  }

  return computeScalarRange(scalarEntry.values as number[]);
}

export default function Page() {
  // TODO(local-auth-bypass): router used for auth redirects.
  const router = useRouter();
  const [dataset, setDataset] = useState<VtkDatasetInfo | null>(null);
  const [streamlines, setStreamlines] = useState<VtkPolylineSet | null>(null);

  const [loadingMesh, setLoadingMesh] = useState(true);
  const [loadingStreamlines, setLoadingStreamlines] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [reValue, setReValue] = useState<number>(75.25);
  const [selectedField, setSelectedField] = useState<ViewFieldKey>(
    'control_velocity_magnitude',
  );

  const [activeScalar, setActiveScalar] = useState<string | null>(null);
  const [streamlineScalar, setStreamlineScalar] = useState<string | null>(null);

  const [sectionSharedRange, setSectionSharedRange] =
    useState<ScalarRange | null>(null);

  const vtkViewerRef = React.useRef<VtkViewerHandle>(null);

  const config = FIELD_CONFIG[selectedField];
  const showStreamlines = config.mode === 'streamlines';
  const meshVisible = config.mode === 'scalar';
  const isBusy = loadingMesh || (showStreamlines ? loadingStreamlines : false);

  const loadMesh = async (reParam?: number, fieldParam?: ViewFieldKey) => {
    setLoadingMesh(true);
    setError(null);

    try {
      // TODO(local-auth-bypass): token validation and login redirect.
      const token = localStorage.getItem('access_token');
      if (!token) {
        router.replace('/login');
        return;
      }

      const apiUrl = process.env.NEXT_PUBLIC_API_URL;
      if (!apiUrl) {
        throw new Error('NEXT_PUBLIC_API_URL is not defined');
      }

      const currentRe = reParam ?? reValue;
      const currentField = fieldParam ?? selectedField;
      const currentConfig = FIELD_CONFIG[currentField];

      const response = await fetch(
        `${apiUrl}/mesh/polydata?re=${encodeURIComponent(
          String(currentRe),
        )}&scalar_field=${encodeURIComponent(currentConfig.scalarField)}`,
        {
          // TODO(local-auth-bypass): Bearer auth header.
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // TODO(local-auth-bypass): 401 handling and token cleanup.
      if (response.status === 401) {
        localStorage.removeItem('access_token');
        document.cookie = 'access_token=; path=/; max-age=0';
        router.replace('/login');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const jsonData: BackendPolyDataResponse = await response.json();
      setDataset(jsonData.mesh);
      setActiveScalar(jsonData.activeScalar ?? null);

      setSectionSharedRange(
        extractScalarRange(jsonData.mesh, 'controlled_velocity_magnitude'),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setDataset(null);
      setActiveScalar(null);
      setSectionSharedRange(null);
    } finally {
      setLoadingMesh(false);
    }
  };

  const loadStreamlines = async (
    reParam?: number,
    fieldParam?: ViewFieldKey,
  ) => {
    const currentRe = reParam ?? reValue;
    const currentField = fieldParam ?? selectedField;
    const currentConfig = FIELD_CONFIG[currentField];

    if (currentConfig.mode !== 'streamlines' || !currentConfig.vectorField) {
      setStreamlines(null);
      setStreamlineScalar(null);
      setLoadingStreamlines(false);
      return;
    }

    setLoadingStreamlines(true);
    setError(null);

    try {
      // TODO(local-auth-bypass): token validation and login redirect.
      const token = localStorage.getItem('access_token');
      if (!token) {
        router.replace('/login');
        return;
      }

      const apiUrl = process.env.NEXT_PUBLIC_API_URL;
      if (!apiUrl) {
        throw new Error('NEXT_PUBLIC_API_URL is not defined');
      }

      const response = await fetch(
        `${apiUrl}/mesh/streamlines?re=${encodeURIComponent(
          String(currentRe),
        )}&vector_field=${encodeURIComponent(currentConfig.vectorField)}`,
        {
          // TODO(local-auth-bypass): Bearer auth header.
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // TODO(local-auth-bypass): 401 handling and token cleanup.
      if (response.status === 401) {
        localStorage.removeItem('access_token');
        document.cookie = 'access_token=; path=/; max-age=0';
        router.replace('/login');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const jsonData: BackendStreamlineResponse = await response.json();
      setStreamlines(jsonData.streamlines);
      setStreamlineScalar(jsonData.colorScalar ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setStreamlines(null);
      setStreamlineScalar(null);
    } finally {
      setLoadingStreamlines(false);
    }
  };

  useEffect(() => {
    void Promise.all([loadMesh(), loadStreamlines()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReChange = (_: Event, value: number | number[]) => {
    if (typeof value !== 'number') return;
    setReValue(value);
  };

  const handleReChangeCommitted = async (
    _: Event,
    value: number | number[],
  ) => {
    if (typeof value !== 'number') return;
    setReValue(value);

    await Promise.all([
      loadMesh(value, selectedField),
      loadStreamlines(value, selectedField),
    ]);
  };

  const handleFieldChange = async (event: SelectChangeEvent<ViewFieldKey>) => {
    const nextField = event.target.value as ViewFieldKey;
    setSelectedField(nextField);

    await Promise.all([
      loadMesh(reValue, nextField),
      loadStreamlines(reValue, nextField),
    ]);
  };

  const vtkViewerForcedRange = useMemo(() => {
    if (
      selectedField === 'control_velocity_magnitude' &&
      (activeScalar === 'controlled_velocity_magnitude' ||
        activeScalar === 'control')
    ) {
      return sectionSharedRange;
    }

    return null;
  }, [selectedField, activeScalar, sectionSharedRange]);

  return (
    <Grid
      container
      spacing={2}
      sx={{
        height: '100%',
        width: '100%',
        p: 3,
        minHeight: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {error && (
        <Grid size={{ xs: 12 }}>
          <Alert severity='error' onClose={() => setError(null)}>
            <strong>Error:</strong> {error}
            <br />
            Check that the backend is running on{' '}
            {process.env.NEXT_PUBLIC_API_URL}
          </Alert>
        </Grid>
      )}

      { dataset && (
        <>
          <Grid
            size={{ xs: 12, md: 3, lg: 2.5 }}
            sx={{ minHeight: 0, display: 'flex', height: '100%' }}
          >
            <ControlPanel
              dataset={dataset}
              reValue={reValue}
              field={selectedField}
              loadingMesh={loadingMesh}
              loadingStreamlines={loadingStreamlines}
              onReChange={handleReChange}
              onReChangeCommitted={handleReChangeCommitted}
              onFieldChange={handleFieldChange}
              onResetCamera={() => vtkViewerRef.current?.resetCamera()}
            />
          </Grid>

          <Grid
            size={{ xs: 12, md: 6, lg: 6.5 }}
            sx={{ minHeight: 0, display: 'flex', height: '100%' }}
          >
            <Box sx={{ height: '100%', width: '100%', minHeight: 0 }}>
              <VtkViewer
                ref={vtkViewerRef}
                dataset={dataset}
                activeScalar={meshVisible ? activeScalar : null}
                activeScalarLabel={
                  meshVisible ? config.activeScalarLabel : null
                }
                streamlines={streamlines}
                streamlineScalar={streamlineScalar}
                streamlineScalarLabel={
                  showStreamlines ? config.activeScalarLabel : null
                }
                showStreamlines={showStreamlines}
                meshVisible={meshVisible}
                status={isBusy ? 'computing' : 'ready'}
                forcedScalarRange={vtkViewerForcedRange}
              />
            </Box>
          </Grid>

          <Grid
            size={{ xs: 12, md: 3, lg: 3 }}
            sx={{ minHeight: 0, display: 'flex', height: '100%' }}
          >
            <SectionViewer
              reValue={reValue}
              sharedScalarRange={sectionSharedRange}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
}
